/**
 * Retailer Selector Component
 * 
 * Dropdown that allows users to switch between:
 * - "All Retailers" (aggregated view)
 * - Individual retailer views (Ulta, JCP, etc.)
 * - DTC Platforms (Shopify, Amazon)
 * 
 * Only shown if company has multiple retailers or DTC platforms.
 */

import React from 'react';
import { Store, ChevronDown, Building2, Home, ShoppingCart } from 'lucide-react';
import { useLocation } from 'wouter';
import { useTenant } from '../contexts/TenantContext';
import { getRetailerInfo } from '../config/retailers';

interface RetailerSelectorProps {
  className?: string;
  showLabel?: boolean;
}

export function RetailerSelector({ 
  className = '', 
  showLabel = true 
}: RetailerSelectorProps) {
  const { 
    retailers, 
    selectedRetailerId, 
    setSelectedRetailerId,
    isLoadingRetailers,
    isMultiRetailer,
    selectedRetailer,
    dtcPlatforms,
    hasDtcPlatforms
  } = useTenant();
  
  const [, setLocation] = useLocation();

  // Don't show selector if only one retailer and no DTC platforms
  if (!isMultiRetailer && retailers.length <= 1 && !hasDtcPlatforms) {
    return null;
  }
  
  // Handle selection change (navigate to DTC if DTC platform selected)
  const handleSelectionChange = (value: string) => {
    if (value.startsWith('dtc-')) {
      // Navigate to DTC overview
      setLocation('/dtc/overview');
    } else {
      // Set retail retailer
      setSelectedRetailerId(value || null);
    }
  };

  // Loading state
  if (isLoadingRetailers) {
    return (
      <div className={`flex items-center space-x-2 animate-pulse ${className}`}>
        <Store className="w-4 h-4 text-muted-foreground" />
        <div className="h-9 w-44 bg-secondary rounded-lg" />
      </div>
    );
  }

  const selectedRetailerInfo = selectedRetailerId ? getRetailerInfo(selectedRetailerId) : null;

  // Detect if we're on a dark/colored background (e.g., purple header)
  // by checking if className contains text-white
  const isOnDarkBackground = className.includes('text-white');

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Icon - changes based on view - inherit color from parent */}
      {selectedRetailerId ? (
        <Store className={`w-4 h-4 flex-shrink-0 ${isOnDarkBackground ? 'text-current' : 'text-muted-foreground'}`} />
      ) : (
        <Home className={`w-4 h-4 flex-shrink-0 ${isOnDarkBackground ? 'text-current' : 'text-muted-foreground'}`} />
      )}
      
      {/* Optional label */}
      {showLabel && (
        <label className={`text-sm font-medium ${isOnDarkBackground ? 'text-current' : 'text-muted-foreground'}`}>
          View:
        </label>
      )}
      
      {/* Dropdown with subtle background */}
      <div className="relative retailer-dropdown">
        <select
          value={selectedRetailerId || ''}
          onChange={(e) => handleSelectionChange(e.target.value)}
          className={`
            appearance-none 
            border 
            rounded-lg 
            px-4 py-2 pr-10 
            text-sm font-medium 
            focus:outline-none 
            focus:ring-2 
            transition-all 
            cursor-pointer
            min-w-[180px]
            ${isOnDarkBackground 
              ? 'border-white/30 hover:border-white/50 focus:ring-white/20 focus:border-white text-white' 
              : 'border-border hover:border-primary/50 focus:ring-primary/20 focus:border-primary'
            }
          `}
          style={{
            backgroundColor: isOnDarkBackground
              ? 'rgba(255, 255, 255, 0.1)' // Semi-transparent white on dark backgrounds
              : selectedRetailerInfo 
              ? selectedRetailerInfo.colors.light 
                : 'rgb(238, 242, 255)' // Light purple for overview on light backgrounds
          }}
          aria-label="Select retailer view"
        >
          {/* "Overview" option - All Retail */}
          <option value="" className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-900">
            🏢 Overview {retailers.length > 1 ? `(${retailers.length} Retailers)` : ''}
          </option>
          
          {/* Individual retailers */}
          {retailers.map(retailer => {
            const info = getRetailerInfo(retailer.id);
            return (
              <option 
                key={retailer.id} 
                value={retailer.id}
                className="text-gray-900"
                style={{ backgroundColor: info?.colors.light }}
              >
                {retailer.name}
              </option>
            );
          })}
          
          {/* DTC Platforms Section */}
          {hasDtcPlatforms && dtcPlatforms && dtcPlatforms.length > 0 && (
            <>
              <option disabled className="text-gray-600">────────────</option>
              <option value="dtc-overview" className="text-gray-900" style={{ backgroundColor: '#f3e8ff' }}>
                🛒 DTC Channel ({dtcPlatforms.length} Platform{dtcPlatforms.length > 1 ? 's' : ''})
              </option>
            </>
          )}
        </select>
        
        {/* Custom dropdown icon - inherit color on dark backgrounds */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className={`w-4 h-4 ${isOnDarkBackground ? 'text-current opacity-80' : 'text-muted-foreground'}`} />
        </div>
      </div>
      
      {/* Visual indicator when filtered to specific retailer */}
      {selectedRetailerId && selectedRetailerInfo && (
        <div 
          className="
            px-2 py-1 
            text-xs font-semibold 
            rounded-md
            border-2
            whitespace-nowrap
          "
          style={{
            backgroundColor: selectedRetailerInfo.colors.light,
            color: selectedRetailerInfo.colors.dark,
            borderColor: selectedRetailerInfo.colors.medium,
          }}
        >
          Filtered
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for mobile/smaller screens
 */
export function RetailerSelectorCompact({ className = '' }: { className?: string }) {
  const { 
    retailers, 
    selectedRetailerId, 
    setSelectedRetailerId,
    isLoadingRetailers,
    isMultiRetailer
  } = useTenant();

  if (!isMultiRetailer && retailers.length <= 1) {
    return null;
  }

  if (isLoadingRetailers) {
    return <div className={`h-9 w-32 bg-secondary rounded-lg animate-pulse ${className}`} />;
  }

  return (
    <div className={`relative ${className}`}>
      <select
        value={selectedRetailerId || ''}
        onChange={(e) => setSelectedRetailerId(e.target.value || null)}
        className="
          appearance-none 
          bg-background 
          border border-border 
          rounded-lg 
          px-3 py-2 pr-8
          text-sm
          w-full
          focus:outline-none 
          focus:ring-2 
          focus:ring-primary/20
        "
      >
        <option value="">All</option>
        {retailers.map(retailer => (
          <option key={retailer.id} value={retailer.id}>
            {retailer.name}
          </option>
        ))}
      </select>
      
      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
    </div>
  );
}

/**
 * Tab-style retailer selector (alternative UI pattern)
 */
export function RetailerTabs({ className = '' }: { className?: string }) {
  const { 
    retailers, 
    selectedRetailerId, 
    setSelectedRetailerId,
    isLoadingRetailers,
    isMultiRetailer
  } = useTenant();

  if (!isMultiRetailer && retailers.length <= 1) {
    return null;
  }

  if (isLoadingRetailers) {
    return <div className={`h-10 w-full bg-secondary rounded-lg animate-pulse ${className}`} />;
  }

  return (
    <div className={`flex items-center space-x-2 overflow-x-auto ${className}`}>
      {/* "All" tab */}
      <button
        onClick={() => setSelectedRetailerId(null)}
        className={`
          px-4 py-2 
          text-sm font-medium 
          rounded-lg 
          transition-all
          whitespace-nowrap
          ${!selectedRetailerId 
            ? 'bg-primary text-primary-foreground shadow-sm' 
            : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
          }
        `}
      >
        All Retailers
      </button>
      
      {/* Individual retailer tabs */}
      {retailers.map(retailer => (
        <button
          key={retailer.id}
          onClick={() => setSelectedRetailerId(retailer.id)}
          className={`
            px-4 py-2 
            text-sm font-medium 
            rounded-lg 
            transition-all
            whitespace-nowrap
            ${selectedRetailerId === retailer.id
              ? 'bg-primary text-primary-foreground shadow-sm' 
              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }
          `}
        >
          {retailer.name}
        </button>
      ))}
    </div>
  );
}

